import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { uniqBy } from 'lodash';
import { useBoolean } from 'usehooks-ts';
import { Autocomplete, Box, Stack, TextField, Typography } from '@mui/material';

import { formatUserName } from 'helper/formatUserName';
import { useDebouncedSearch } from 'hooks/useDebouncedSearch';
import { menuStyles } from 'components/simple/MenuPanel';
import { ArrowFilledDownIcon } from 'assets/icons';

const NoOptionsOverlay = ({ description }) => {
  return (
    <Stack spacing={2}>
      <Typography variant="body2-bold">No results found</Typography>
      <Typography variant="body2">{description}</Typography>
    </Stack>
  );
};

NoOptionsOverlay.propTypes = {
  description: PropTypes.string.isRequired,
};

const styles = {
  autocomplete: ({ spacing }) => ({
    padding: spacing(2, 0),
  }),
  paper: ({ spacing, palette }) => ({
    borderRadius: 2.5,
    marginTop: 0.5,
    '.MuiAutocomplete-noOptions': {
      padding: spacing(1, 2),
      border: `2px solid ${palette.colors.darkPale}`,
    },
  }),
  listbox: ({ spacing, palette, typography }) => ({
    padding: 'unset',
    '.MuiAutocomplete-option': {
      ...typography.body2,
      display: 'block',
      padding: spacing(1, 2),
      borderBottom: `2px solid ${palette.colors.darkPale}`,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      ...menuStyles.autocompleteOption({ palette }),
      '&:last-of-type': {
        borderBottom: 'unset',
      },
    },
  }),
  input: ({ palette, typography }) => ({
    '.MuiInputBase-root': {
      borderRadius: '100px !important',
      backgroundColor: palette.colors.lightPale,
      border: `2px solid ${palette.colors.darkPale} !important`,
      '&.Mui-error': {
        borderColor: `${palette.error.main} !important`,
      },
      '.MuiOutlinedInput-notchedOutline': {
        border: 'none',
      },
    },
    '.MuiInputBase-input': typography.body2,
  }),
  popupIcon: ({ palette }) => ({
    color: palette.colors.darkPale,
    width: 12,
    height: 12,
  }),
  popupIndicator: {
    marginRight: 0,
  },
};

export const DataGridAutocomplete = ({ noOptionsDescription, params, fetchOptions }) => {
  useEffect(() => {
    const virtualScroller = params.api.rootElementRef?.current?.querySelector('.MuiDataGrid-virtualScroller');

    const prevOverflow = virtualScroller?.style.overflow;

    if (virtualScroller) {
      virtualScroller.style.overflow = 'hidden';
    }

    const preventScroll = (e) => {
      if (e.target.closest('.MuiAutocomplete-listbox')) {
        return;
      }

      e.preventDefault();
    };

    document.addEventListener('wheel', preventScroll, { passive: false });
    document.addEventListener('touchmove', preventScroll, { passive: false });

    return () => {
      if (virtualScroller) {
        virtualScroller.style.overflow = prevOverflow;
      }

      document.removeEventListener('wheel', preventScroll);
      document.removeEventListener('touchmove', preventScroll);
    };
  }, [params.api]);

  const [optionsList, setOptionsList] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [page, setPage] = useState(1);
  const { value: hasMore, setValue: setHasMore } = useBoolean();
  const { value: isLoading, setTrue: startLoading, setFalse: stopLoading } = useBoolean();
  const listboxRef = useRef(null);
  const fetchOptionsRef = useRef(fetchOptions);

  fetchOptionsRef.current = fetchOptions;

  const resetSearch = () => {
    setPage(1);
    setOptionsList([]);
  };

  const { debouncedValue: debouncedSearchValue } = useDebouncedSearch(searchValue, {
    onDebouncedValueChanged: resetSearch,
  });

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      startLoading();

      try {
        const data = await fetchOptionsRef.current({ page, search: debouncedSearchValue });

        if (cancelled) {
          return;
        }

        const results = data?.results || [];

        setHasMore(!!data?.next);

        if (page === 1) {
          setOptionsList(results);
        } else {
          setOptionsList((prev) => uniqBy([...prev, ...results], 'id'));
        }
      } finally {
        if (!cancelled) {
          stopLoading();
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [page, debouncedSearchValue, startLoading, stopLoading, setHasMore]);

  const handleScroll = (event) => {
    const listboxNode = event.currentTarget;

    if (listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 50 && hasMore && !isLoading) {
      setPage((prev) => prev + 1);
    }
  };

  return (
    <Autocomplete
      open
      fullWidth
      sx={styles.autocomplete}
      popupIcon={<Box component={ArrowFilledDownIcon} sx={styles.popupIcon} />}
      value={params.value}
      inputValue={searchValue}
      onInputChange={(_, newInputValue) => {
        setSearchValue(newInputValue);
      }}
      onClose={(_event, reason) => {
        if (reason !== 'selectOption') {
          params.api.stopCellEditMode({
            id: params.id,
            field: params.field,
            ignoreModifications: true,
          });
        }
      }}
      onChange={async (event, newValue) => {
        await params.api.setEditCellValue({
          id: params.id,
          field: params.field,
          value: newValue || null,
        });
        params.api.stopCellEditMode({ id: params.id, field: params.field });
      }}
      options={optionsList}
      filterOptions={(x) => x}
      loading={isLoading}
      getOptionLabel={(option) => formatUserName(option)}
      isOptionEqualToValue={(option, value) => option?.id === value?.id}
      noOptionsText={<NoOptionsOverlay description={noOptionsDescription} />}
      slotProps={{
        listbox: { ref: listboxRef, onScroll: handleScroll, sx: styles.listbox },
        paper: { sx: styles.paper },
        popupIndicator: { sx: styles.popupIndicator },
      }}
      renderInput={(inputParams) => (
        <TextField
          {...inputParams}
          autoFocus
          error={params.error}
          size="small"
          placeholder="Assign owner..."
          sx={styles.input}
        />
      )}
    />
  );
};

const ownerPropTypes = {
  id: PropTypes.number,
  full_name: PropTypes.string,
  email: PropTypes.string,
};

DataGridAutocomplete.propTypes = {
  noOptionsDescription: PropTypes.string.isRequired,
  params: PropTypes.shape({
    value: PropTypes.shape(ownerPropTypes),
    error: PropTypes.bool,
    id: PropTypes.string.isRequired,
    field: PropTypes.string.isRequired,
    api: PropTypes.shape({
      setEditCellValue: PropTypes.func.isRequired,
      stopCellEditMode: PropTypes.func.isRequired,
    }),
  }).isRequired,
  fetchOptions: PropTypes.func.isRequired,
};
